import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import html2canvas from "html2canvas";
import { useUser } from "../../context/UserContext";
import { teams as teamsService, kits as kitsService } from "../../services/supabase";
import { Team } from "../../types/database";
import { BackPrintConfig } from "../../types/props";
import SiteNav from "../../components/SiteNav";
import "./createkit.css";

import tshirtArcticWhiteFront from "../../assets/t-shirts/Front/JC001-ARCTIC-WHITE-1776042201.webp";
import tshirtCharcoalFront from "../../assets/t-shirts/Front/JC001-CHARCOAL-1776042221.webp";
import tshirtElectricPinkFront from "../../assets/t-shirts/Front/JC001-ELECTRIC-PINK-1776042252.webp";
import tshirtHawaiianBlueFront from "../../assets/t-shirts/Front/JC001-HAWAIIAN-BLUE-1776042275.webp";
import tshirtJadeFront from "../../assets/t-shirts/Front/JC001-JADE-1776042296.webp";
import tshirtJetBlackFront from "../../assets/t-shirts/Front/JC001-JET-BLACK-1776042300.webp";
import tshirtMagentaMagicFront from "../../assets/t-shirts/Front/JC001-MAGENTA-MAGIC-1776042315.webp";
import tshirtOrangeCrushFront from "../../assets/t-shirts/Front/JC001-ORANGE-CRUSH-1776042327.webp";
// @ts-ignore
import tshirtFrenchNavyFront from "../../assets/t-shirts/Front/JC001J FRENCH NAVY (FRONT).webp";

// @ts-ignore
import tshirtArcticWhiteBack from "../../assets/t-shirts/Back/JC001J ARCTIC WHITE (BACK).webp";
// @ts-ignore
import tshirtCharcoalBack from "../../assets/t-shirts/Back/JC001J CHARCOAL (BACK).webp";
// @ts-ignore
import tshirtElectricPinkBack from "../../assets/t-shirts/Back/JC001J ELECTRIC PINK (BACK).webp";
// @ts-ignore
import tshirtHawaiianBlueBack from "../../assets/t-shirts/Back/JC001 HAWAIIIAN (BACK).webp";
// @ts-ignore
import tshirtJadeBack from "../../assets/t-shirts/Back/JC001J JADE (BACK).webp";
// @ts-ignore
import tshirtJetBlackBack from "../../assets/t-shirts/Back/JC001J JET BLACK (BACK).webp";
// @ts-ignore
import tshirtMagentaMagicBack from "../../assets/t-shirts/Back/JC001J MAGENTA MAGIC (BACK).webp";
// @ts-ignore
import tshirtOrangeCrushBack from "../../assets/t-shirts/Back/JC001 ORANGE CRUSH (BACK).webp";
// @ts-ignore
import tshirtFrenchNavyBack from "../../assets/t-shirts/Back/JC001J FRENCH NAVY (BACK).webp";

import tribeletLogoWhite from "../../assets/Polos/white_kit_logo.png";
import tribeletLogoBlack from "../../assets/Polos/black_kit_logo.png";

const colorOptions = [
  { hex: "#F2F2F0", label: "Arctic White" },
  { hex: "#4A4A4A", label: "Charcoal" },
  { hex: "#FF1493", label: "Electric Pink" },
  { hex: "#0099CC", label: "Hawaiian Blue" },
  { hex: "#00A878", label: "Jade" },
  { hex: "#111111", label: "Jet Black" },
  { hex: "#CC00CC", label: "Magenta Magic" },
  { hex: "#FF5500", label: "Orange Crush" },
  { hex: "#1E3A8A", label: "French Navy" },
];

const TSHIRT_FRONT_MAP: Record<string, string> = {
  "#F2F2F0": tshirtArcticWhiteFront, "#4A4A4A": tshirtCharcoalFront,
  "#FF1493": tshirtElectricPinkFront, "#0099CC": tshirtHawaiianBlueFront,
  "#00A878": tshirtJadeFront, "#111111": tshirtJetBlackFront,
  "#CC00CC": tshirtMagentaMagicFront, "#FF5500": tshirtOrangeCrushFront,
  "#1E3A8A": tshirtFrenchNavyFront,
};

const TSHIRT_BACK_MAP: Record<string, string> = {
  "#F2F2F0": tshirtArcticWhiteBack, "#4A4A4A": tshirtCharcoalBack,
  "#FF1493": tshirtElectricPinkBack, "#0099CC": tshirtHawaiianBlueBack,
  "#00A878": tshirtJadeBack, "#111111": tshirtJetBlackBack,
  "#CC00CC": tshirtMagentaMagicBack, "#FF5500": tshirtOrangeCrushBack,
  "#1E3A8A": tshirtFrenchNavyBack,
};

const isLightColor = (hex: string) => hex === "#F2F2F0";

const imgSrc = (src: string) =>
  src.startsWith("data:") || src.startsWith("http") ? src : `data:image/png;base64,${src}`;

const CreateKit: React.FC = () => {
  const { user, username } = useUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const teamId = searchParams.get("teamId");

  const [team, setTeam] = useState<Team | null>(null);

  const [teamwearColor, setTeamwearColor] = useState("#1E3A8A");
  const [emblemColor, setEmblemColor] = useState("#FFFFFF");
  const [manualEmblemColor, setManualEmblemColor] = useState(false);
  const [manualPrintColor, setManualPrintColor] = useState(false);
  const [backPrint, setBackPrint] = useState<BackPrintConfig>({
    enabled: false, text: "", font: "Arial", scale: 50, color: "#FFFFFF", position: 50,
  });
  const [backImageScale, setBackImageScale] = useState(150);
  const [designName, setDesignName] = useState("");

  const [frontLoaded, setFrontLoaded] = useState(false);
  const [backLoaded, setBackLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  const frontRef = useRef<HTMLDivElement>(null);
  const backRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!username) return;
    if (!teamId) { navigate("/"); return; }
    teamsService.getById(teamId)
      .then(({ data, error }) => {
        if (error || !data) throw error || new Error("Team not found");
        setTeam(data);
        setBackPrint(prev => ({ ...prev, text: data.team_name.toUpperCase() }));
      })
      .catch(() => navigate("/"));
  }, [username, teamId, navigate]);

  useEffect(() => {
    setFrontLoaded(false);
    setBackLoaded(false);
    if (!manualPrintColor) {
      updateBackPrint("color", isLightColor(teamwearColor) ? "#000000" : "#FFFFFF");
    }
  }, [teamwearColor]);

  const hasBackImage = !!team?.logo_url;
  const positionMin = hasBackImage ? 50 : 15;

  useEffect(() => {
    if (backPrint.position < positionMin) {
      updateBackPrint("position", positionMin);
    }
  }, [hasBackImage]);

  const updateBackPrint = <K extends keyof BackPrintConfig>(field: K, value: BackPrintConfig[K]) => {
    setBackPrint(prev => ({ ...prev, [field]: value }));
  };

  const getFrontImg = () => TSHIRT_FRONT_MAP[teamwearColor] || tshirtArcticWhiteFront;
  const getBackImg = () => TSHIRT_BACK_MAP[teamwearColor] || tshirtArcticWhiteBack;

  const getTribeletLogo = () => {
    if (manualEmblemColor) return emblemColor === "#FFFFFF" ? tribeletLogoWhite : tribeletLogoBlack;
    return isLightColor(teamwearColor) ? tribeletLogoBlack : tribeletLogoWhite;
  };

  const bothLoaded = frontLoaded && backLoaded;

  const capturePreviewImage = async (element: HTMLDivElement | null): Promise<string> => {
    if (!element) throw new Error("Element not found");
    const images = element.querySelectorAll("img");
    await Promise.all(
      Array.from(images).map(img => {
        if (img.complete && img.naturalHeight !== 0) return Promise.resolve();
        return new Promise<void>(resolve => {
          img.onload = () => resolve();
          img.onerror = () => resolve();
          setTimeout(() => resolve(), 5000);
        });
      })
    );
    await new Promise(resolve => setTimeout(resolve, 500));
    const canvas = await html2canvas(element, {
      backgroundColor: null,
      scale: 4,
      useCORS: true,
      allowTaint: false,
      logging: false,
    } as any);
    return canvas.toDataURL("image/png");
  };

  const handleSave = async () => {
    if (!user || !team || !teamId) return;
    setSaving(true);
    try {
      const frontPreview = await capturePreviewImage(frontRef.current);
      const backPreview = await capturePreviewImage(backRef.current);

      const kitId = crypto.randomUUID();

      const { data: frontUrl } = await kitsService.uploadPreviewImage(kitId, frontPreview, "front");
      const { data: backUrl } = await kitsService.uploadPreviewImage(kitId, backPreview, "back");

      let backImageUrl: string | null = null;
      if (team.logo_url) {
        const { data } = await kitsService.uploadPreviewImage(kitId, team.logo_url, "back");
        backImageUrl = data;
      }

      const { error } = await kitsService.create({
        kit_id: kitId,
        team_id: teamId,
        design_name: designName || "Untitled Design",
        created_by_email: user.email!,
        status: "draft",
        kit_type: "tshirt",
        teamwear_color: teamwearColor,
        emblem_color: emblemColor,
        front_image_url: null,
        front_image_show: true,
        front_image_scale: 100,
        back_image_url: backImageUrl,
        back_image_show: !!team.logo_url,
        back_image_scale: backImageScale,
        back_print_enabled: backPrint.enabled,
        back_print_text: backPrint.text || null,
        back_print_font: backPrint.font || null,
        back_print_scale: backPrint.scale,
        back_print_position: backPrint.position,
        preview_front_url: frontUrl,
        preview_back_url: backUrl,
        team_name: team.team_name,
      });
      if (error) throw error;

      navigate(`/team/${teamId}?tab=kit`);
    } catch (err) {
      console.error("Failed to save kit:", err);
      alert(`Failed to save kit design: ${err instanceof Error ? err.message : "Unknown error"}`);
      setSaving(false);
    }
  };

  if (!username) {
    return (
      <div className="ck-page">
        <div className="grain-overlay" />
        <SiteNav />
        <div className="ck-content">
          <div className="ck-gated">
            <div className="ck-gated-title">Sign In Required</div>
            <p className="ck-gated-desc">Please log in or create an account to customise a kit.</p>
            <button className="ck-btn" onClick={() => navigate("/account")}>
              Go to Sign In →
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ck-page">
      <div className="grain-overlay" />
      <SiteNav />

      <div className="ck-progress">
        {teamId && (
          <button className="ck-back" onClick={() => navigate(`/team/${teamId}`)}>
            ← Back to Team Portal
          </button>
        )}
      </div>

      <div className="ck-content">
        <div className="ck-eyebrow">[ New Kit ]</div>
        <h1 className="ck-view-title">Design your kit.</h1>
        <p className="ck-view-subtitle">Choose colours and set your back print.</p>

        <div className="ck-designer">

          {/* LEFT CONTROL PANEL */}
          <aside className="ck-ctrl-panel">

            {/* Teamwear colour */}
            <div className="ck-ctrl-row">
              <label className="ck-ctrl-label">Teamwear colour</label>
              <div className="ck-ctrl-body">
                <div className="ck-dot-swatches">
                  {colorOptions.map(c => (
                    <div
                      key={c.hex}
                      className={`ck-dot-swatch ${teamwearColor === c.hex ? "selected" : ""}`}
                      style={{ backgroundColor: c.hex }}
                      title={c.label}
                      onClick={() => {
                        setTeamwearColor(c.hex);
                        if (!manualEmblemColor) setEmblemColor(isLightColor(c.hex) ? "#000000" : "#FFFFFF");
                        if (!manualPrintColor) updateBackPrint("color", isLightColor(c.hex) ? "#000000" : "#FFFFFF");
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Emblem colour */}
            <div className="ck-ctrl-row">
              <label className="ck-ctrl-label">Emblem colour</label>
              <div className="ck-ctrl-body">
                <div className="ck-dot-swatches">
                  <div
                    className={`ck-dot-swatch ${emblemColor === "#FFFFFF" ? "selected" : ""}`}
                    style={{ backgroundColor: "#FFFFFF" }}
                    title="White"
                    onClick={() => { setEmblemColor("#FFFFFF"); setManualEmblemColor(true); }}
                  />
                  <div
                    className={`ck-dot-swatch ${emblemColor === "#000000" ? "selected" : ""}`}
                    style={{ backgroundColor: "#000000" }}
                    title="Black"
                    onClick={() => { setEmblemColor("#000000"); setManualEmblemColor(true); }}
                  />
                </div>
              </div>
            </div>

            {/* Back image scale (only if the team has a logo) */}
            {team?.logo_url && (
              <div className="ck-ctrl-row">
                <label className="ck-ctrl-label">
                  Back Image
                  <span className="ck-ctrl-sublabel">Centre back — your team logo</span>
                </label>
                <div className="ck-ctrl-body">
                  <div className="ck-slider-mini">
                    <span className="ck-slider-mini-label">Scale</span>
                    <input type="range" min="25" max="150" value={backImageScale} onChange={e => setBackImageScale(parseInt(e.target.value))} />
                    <span className="ck-slider-mini-val">{backImageScale}%</span>
                  </div>
                </div>
              </div>
            )}

            {/* Back print */}
            <div className="ck-ctrl-row">
              <label className="ck-ctrl-label">Back Print</label>
              <div className="ck-ctrl-body">
                <label className="ck-checkbox-line">
                  <input type="checkbox" checked={backPrint.enabled} onChange={e => updateBackPrint("enabled", e.target.checked)} />
                  <span className="ck-checkmark" />
                  <span className="ck-checkbox-text">Add text</span>
                </label>

                {backPrint.enabled && (
                  <>
                    <input
                      type="text"
                      className="ck-ctrl-input"
                      placeholder="e.g. Sunday FC 2025"
                      value={backPrint.text}
                      maxLength={20}
                      onChange={e => updateBackPrint("text", e.target.value)}
                    />
                    <select
                      className="ck-ctrl-input"
                      value={backPrint.font}
                      onChange={e => updateBackPrint("font", e.target.value)}
                    >
                      <option value="Arial">Arial</option>
                      <option value="Helvetica">Helvetica</option>
                      <option value="Times">Times</option>
                      <option value="Georgia">Georgia</option>
                    </select>
                    <label className="ck-ctrl-label" style={{ marginTop: 8 }}>Text colour</label>
                    <div className="ck-dot-swatches">
                      <div
                        className={`ck-dot-swatch ${backPrint.color === "#FFFFFF" ? "selected" : ""}`}
                        style={{ backgroundColor: "#FFFFFF" }}
                        title="White"
                        onClick={() => { updateBackPrint("color", "#FFFFFF"); setManualPrintColor(true); }}
                      />
                      <div
                        className={`ck-dot-swatch ${backPrint.color === "#000000" ? "selected" : ""}`}
                        style={{ backgroundColor: "#000000" }}
                        title="Black"
                        onClick={() => { updateBackPrint("color", "#000000"); setManualPrintColor(true); }}
                      />
                    </div>
                    <div className="ck-slider-mini">
                      <span className="ck-slider-mini-label">Scale</span>
                      <input type="range" min="25" max="100" value={backPrint.scale} onChange={e => updateBackPrint("scale", parseInt(e.target.value))} />
                      <span className="ck-slider-mini-val">{backPrint.scale}%</span>
                    </div>
                    <div className="ck-slider-mini">
                      <span className="ck-slider-mini-label">Position</span>
                      <input type="range" min={positionMin} max="65" value={backPrint.position} onChange={e => updateBackPrint("position", parseInt(e.target.value))} />
                      <span className="ck-slider-mini-val">{backPrint.position}%</span>
                    </div>
                  </>
                )}
              </div>
            </div>

          </aside>

          {/* RIGHT PREVIEW */}
          <div>
            <div className="ck-preview-stage">
              {!bothLoaded && (
                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                  <span className="ck-loading-text">crafting...</span>
                </div>
              )}
              <div className="ck-preview-pair">

                {/* FRONT */}
                <div className="ck-preview-card">
                  <div className="ck-preview-card-label">Front</div>
                  <div ref={frontRef} style={{ position: "relative", background: "transparent", borderRadius: 4, minHeight: bothLoaded ? 0 : 220 }}>
                    <img
                      src={getFrontImg()}
                      alt="T-Shirt Front"
                      style={{ width: "100%", height: "auto", display: "block", filter: "drop-shadow(0 4px 12px rgba(10,10,10,0.1))", opacity: bothLoaded ? 1 : 0, transition: "opacity 0.25s ease" }}
                      onLoad={() => setFrontLoaded(true)}
                    />
                    {bothLoaded && (
                      <>
                        <img src={getTribeletLogo()} alt="Tribelet emblem" style={{ position: "absolute", top: "28%", left: "37%", width: "8%", height: "auto", objectFit: "contain", transform: "translate(-50%, -50%)" }} />
                        {team?.logo_url && (
                          <img
                            src={imgSrc(team.logo_url)}
                            alt="Team logo"
                            crossOrigin="anonymous"
                            style={{ position: "absolute", top: "28%", left: "62%", width: "15%", height: "auto", objectFit: "contain", transform: "translate(-50%, -50%)", transformOrigin: "center" }}
                          />
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* BACK */}
                <div className="ck-preview-card">
                  <div className="ck-preview-card-label">Back</div>
                  <div ref={backRef} style={{ position: "relative", background: "transparent", borderRadius: 4, minHeight: bothLoaded ? 0 : 220 }}>
                    <img
                      src={getBackImg()}
                      alt="T-Shirt Back"
                      style={{ width: "100%", height: "auto", display: "block", filter: "drop-shadow(0 4px 12px rgba(10,10,10,0.1))", opacity: bothLoaded ? 1 : 0, transition: "opacity 0.25s ease" }}
                      onLoad={() => setBackLoaded(true)}
                    />
                    {bothLoaded && (
                      <>
                        {team?.logo_url && (
                          <img
                            src={imgSrc(team.logo_url)}
                            alt="Team logo"
                            crossOrigin="anonymous"
                            style={{ position: "absolute", top: "30%", left: "50%", width: `${backImageScale * 0.18}%`, height: "auto", objectFit: "contain", transform: `translate(-50%, -50%) scale(${backImageScale / 100})`, transformOrigin: "center" }}
                          />
                        )}
                        {backPrint.enabled && backPrint.text && (
                          <div style={{ position: "absolute", top: `${backPrint.position}%`, left: "50%", transform: "translateX(-50%)", width: "50%", textAlign: "center", fontSize: `${Math.min(backPrint.scale * 0.3, 24)}px`, fontFamily: backPrint.font, color: backPrint.color, fontWeight: "bold", wordBreak: "break-word", lineHeight: 1.2 }}>
                            {backPrint.text}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

              </div>
            </div>

            <div className="ck-field">
              <label className="ck-field-label">Design name</label>
              <input
                type="text"
                className="ck-field-input"
                placeholder="e.g. Home Kit 2025"
                value={designName}
                onChange={e => setDesignName(e.target.value)}
              />
            </div>

            <div className="ck-btn-row">
              <button className="ck-btn-text" onClick={() => navigate(`/team/${teamId}`)} disabled={saving}>
                ← Cancel
              </button>
              <button className="ck-btn" onClick={handleSave} disabled={!bothLoaded || saving}>
                {saving ? "Processing…" : "Save Kit Design →"}
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default CreateKit;
